import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface Notification {
  id: string;
  type: "success" | "error" | "warning" | "info";
  title: string;
  message?: string;
  duration?: number;
  actions?: Array<{ label: string; action: () => void }>;
}

export interface NotificationContextValue {
  notifications: Notification[];
  showNotification: (notification: Omit<Notification, "id">) => void;
  dismissNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const dismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const showNotification = useCallback(
    (notification: Omit<Notification, "id">) => {
      const id = crypto.randomUUID();
      const newNotification: Notification = { ...notification, id };
      setNotifications((prev) => [...prev, newNotification]);

      if (notification.duration !== 0) {
        setTimeout(() => {
          dismissNotification(id);
        }, notification.duration ?? 5000);
      }
    },
    [dismissNotification],
  );

  const value = useMemo<NotificationContextValue>(
    () => ({ notifications, showNotification, dismissNotification }),
    [notifications, showNotification, dismissNotification],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (ctx === null) {
    throw new Error(
      "useNotificationContext must be used within a NotificationProvider",
    );
  }
  return ctx;
}
